import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useCopy } from '@/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

type FooterLink = { label: string; href?: string };

type FooterCopy = {
  tagline?: string;
  copyright?: string;
  columns?: Array<{
    title: string;
    links: Array<string | FooterLink>;
  }>;
  newsletter_title?: string;
  newsletter_placeholder?: string;
  newsletter_note?: string;
  newsletter_success?: string;
  newsletter_error?: string;
};

const FOOTER_LINK_HREFS: Record<string, string> = {
  'How it works': '/#how',
  Situations: '/#situations',
  Languages: '/languages',
  Offline: '/#offline',
  Pricing: '/pricing',
  'Forever Pack': '/pricing#pricing',
  About: '/about',
  Mission: '/#manifesto',
  Press: '/#testimonials',
  Careers: '/signup',
  Contact: '/contact',
  'Help center': '/#faq',
  FAQ: '/#faq',
  'Travel guides': '/blog',
  Blog: '/blog',
  Privacy: '/login',
  Terms: '/login',
  Cookies: '/login',
  Accessibility: '/#faq',
};

function normalizeLinks(links: Array<string | FooterLink> | undefined): FooterLink[] {
  if (!Array.isArray(links)) return [];
  return links.flatMap((link) => {
    if (typeof link === 'string') {
      return [{ label: link, href: FOOTER_LINK_HREFS[link] ?? '/' }];
    }
    if (link && typeof link === 'object' && typeof link.label === 'string') {
      return [{ label: link.label, href: link.href ?? FOOTER_LINK_HREFS[link.label] ?? '/' }];
    }
    return [];
  });
}

// Muted variants of the footer foreground that stay correct in dark mode
// (the old footer hardcoded paper-tinted rgba values).
const FG_70 = 'color-mix(in srgb, var(--paper) 70%, transparent)';
const FG_55 = 'color-mix(in srgb, var(--paper) 55%, transparent)';
const FG_45 = 'color-mix(in srgb, var(--paper) 45%, transparent)';
const FG_LINE = 'color-mix(in srgb, var(--paper) 18%, transparent)';

type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'error';

export function Footer() {
  const copy = useCopy();
  const c = copy<FooterCopy>('footer') ?? {};
  const columns = Array.isArray(c.columns) ? c.columns : [];
  const reduceMotion = useReducedMotion();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<NewsletterStatus>('idle');

  async function onSubscribe(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  }

  const itemVariants = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
      };

  return (
    <footer style={{ background: 'var(--ink)', color: 'var(--paper)' }}>
      <motion.div
        initial={reduceMotion ? false : 'hidden'}
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={reduceMotion ? undefined : { hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      >
        {/* Headline */}
        {c.tagline ? (
          <div style={{ maxWidth: 1180, margin: '0 auto', padding: '48px var(--pad-section-x) 0' }}>
            <motion.h2
              variants={itemVariants}
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 500,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                fontSize: 'clamp(28px, 4.5vw, 52px)',
                maxWidth: '24ch',
                paddingBottom: 48,
              }}
            >
              {c.tagline}
            </motion.h2>
          </div>
        ) : null}

        {/* Bordered band: newsletter + link columns */}
        <div style={{ borderBlock: `1px solid ${FG_LINE}` }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 var(--pad-section-x)' }}>
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr]"
            >
              <div
                className="border-b lg:border-b-0 lg:border-e"
                style={{ borderColor: FG_LINE, padding: '32px 0' }}
              >
                <div className="lg:pe-8">
                  <h3 style={{ fontSize: 17, fontWeight: 500, marginBottom: 18 }}>
                    {c.newsletter_title ?? 'Travel tips and new scenarios, in your inbox.'}
                  </h3>
                  {status === 'success' ? (
                    <p style={{ fontSize: 14, color: FG_70 }}>
                      {c.newsletter_success ?? 'Subscribed — talk soon.'}
                    </p>
                  ) : (
                    <form onSubmit={onSubscribe} className="mb-4 flex">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={c.newsletter_placeholder ?? 'Enter your email address'}
                        aria-label={c.newsletter_placeholder ?? 'Enter your email address'}
                        className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm focus:outline-none"
                        style={{
                          border: `1px solid ${FG_LINE}`,
                          borderInlineEnd: 'none',
                          color: 'var(--paper)',
                        }}
                      />
                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        aria-label="Subscribe"
                        className="flex items-center justify-center px-4 transition-opacity hover:opacity-80 disabled:opacity-50"
                        style={{ border: `1px solid ${FG_LINE}`, background: 'var(--coral)' }}
                      >
                        <ArrowRight className="h-5 w-5 rtl:rotate-180" style={{ color: '#fff' }} />
                      </button>
                    </form>
                  )}
                  {status === 'error' ? (
                    <p
                      role="alert"
                      style={{ fontSize: 12, color: 'var(--coral)', marginBottom: 8 }}
                    >
                      {c.newsletter_error ?? 'Something went wrong. Please try again.'}
                    </p>
                  ) : null}
                  {status !== 'success' ? (
                    <p style={{ fontSize: 12, color: FG_55 }}>
                      {c.newsletter_note ??
                        'By signing up you agree to receive our emails. Unsubscribe any time.'}
                    </p>
                  ) : null}
                  <div style={{ marginTop: 18 }}>
                    <LanguageSwitcher tone="inverse" />
                  </div>
                </div>
              </div>

              <div className="lg:ps-8" style={{ padding: '32px 0' }}>
                <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                  {columns.map((col, i) => (
                    <div key={i}>
                      <h4
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          letterSpacing: '.12em',
                          textTransform: 'uppercase',
                          color: FG_55,
                          marginBottom: 12,
                        }}
                      >
                        {col.title}
                      </h4>
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'grid',
                          gap: 10,
                        }}
                      >
                        {normalizeLinks(col.links).map((l, j) => (
                          <li key={j}>
                            <a
                              href={l.href ?? '#'}
                              className="transition-opacity hover:opacity-100"
                              style={{
                                color: 'var(--paper)',
                                opacity: 0.85,
                                textDecoration: 'none',
                                fontSize: 14,
                              }}
                            >
                              {l.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Big wordmark + copyright */}
        <div
          style={{ maxWidth: 1180, margin: '0 auto', padding: '32px var(--pad-section-x) 28px' }}
        >
          <motion.div variants={itemVariants}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  fontSize: 'clamp(44px, 8vw, 96px)',
                  lineHeight: 1,
                }}
              >
                whatimean
              </span>
              <span
                style={{
                  color: 'var(--coral)',
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(44px, 8vw, 96px)',
                  lineHeight: 1,
                }}
              >
                .
              </span>
            </div>
            {c.copyright ? (
              <p
                style={{
                  marginTop: 24,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: FG_45,
                }}
              >
                {c.copyright}
              </p>
            ) : null}
          </motion.div>
        </div>
      </motion.div>
    </footer>
  );
}
